import type { BotActionFeedback } from './botControlActions'

/**
 * Parses the `close_positions_manually` 409 body into user-facing wording.
 *
 * Read-only sources (spotbot.api.grabbitbot.com, ticket 2762bffe):
 *  - `app/engine/manual.py` `ManualCloseResult.problems()` — per-pair
 *    `manualsell_failed(C1,C2)` / `manualsell_pending(C1,C2)`, plus the
 *    generator warnings `coinconfig_not_found(C1,C2)` /
 *    `coinprice_is_empty(C1,C2,<price|null>)` (`app/engine/risk.py`).
 *  - `app/live_engine.py` `close_positions_manually()` — `bot_halted` /
 *    `bot_run_in_progress` (RunLocked/BotHalted, no pair) and
 *    `manualsell_exception(<ExceptionClassName>)` when the whole request
 *    failed before any per-pair outcome was known.
 *  - `app/router.py`: `EngineConflict(body, status)` is rendered as
 *    `LegacyResult(exc.body, exc.status)` — the HTTP body is the BARE array
 *    (`["manualsell_failed(BTC,USDC)", ...]`), not an `{error: ...}` object.
 *  - api.grabbitbot.com `app/spotbot_proxy.py` `proxy_service_response()`
 *    forwards that status + body byte-for-byte to the portal.
 *
 * Deviation from legacy (documented in manual.py's module docstring,
 * ticket 2a6b2de4 / audit 68ebf311): legacy caught every exception and
 * always answered 200 `[]`, so a failed sell looked like success. The
 * portal must never repeat that — a 409 here is NEVER rendered as success,
 * and the pairs that were not sold are named explicitly. `pending` means
 * the outcome is not known yet (reconciled, or a pending LIMIT order) and
 * will not be re-sent automatically — that must read differently from a
 * hard failure, not as "closed".
 */

export type ManualCloseProblemKind =
  | 'failed'
  | 'pending'
  | 'config_missing'
  | 'price_missing'
  | 'exception'
  | 'bot_halted'
  | 'bot_run_in_progress'
  | 'other'

export interface ManualCloseProblem {
  raw: string
  pair?: string
  kind: ManualCloseProblemKind
}

const PAIR_FAILED = /^manualsell_failed\(([^,)]+),([^,)]+)\)$/
const PAIR_PENDING = /^manualsell_pending\(([^,)]+),([^,)]+)\)$/
const CONFIG_MISSING = /^coinconfig_not_found\(([^,)]+),([^,)]+)\)$/
const PRICE_MISSING = /^coinprice_is_empty\(([^,)]+),([^,)]+),/
const EXCEPTION = /^manualsell_exception\(/

export function parseManualCloseProblem(token: string): ManualCloseProblem {
  let m = PAIR_FAILED.exec(token)
  if (m) return { raw: token, pair: `${m[1]}/${m[2]}`, kind: 'failed' }
  m = PAIR_PENDING.exec(token)
  if (m) return { raw: token, pair: `${m[1]}/${m[2]}`, kind: 'pending' }
  m = CONFIG_MISSING.exec(token)
  if (m) return { raw: token, pair: `${m[1]}/${m[2]}`, kind: 'config_missing' }
  m = PRICE_MISSING.exec(token)
  if (m) return { raw: token, pair: `${m[1]}/${m[2]}`, kind: 'price_missing' }
  if (EXCEPTION.test(token)) return { raw: token, kind: 'exception' }
  if (token === 'bot_halted') return { raw: token, kind: 'bot_halted' }
  if (token === 'bot_run_in_progress') return { raw: token, kind: 'bot_run_in_progress' }
  return { raw: token, kind: 'other' }
}

const pairLabel = (p: ManualCloseProblem): string => p.pair ?? p.raw

/**
 * `raw` is `ApiClientError.raw` for a 409 whose body IS a bare JSON array
 * (see `describeBotActionError`, which only routes here for that shape —
 * the `{error: "bot_action_in_progress"}` / `{error: "no_amount"}` object
 * shapes are handled before this is ever reached).
 */
export function describeManualCloseConflict(raw: unknown[]): BotActionFeedback {
  const problems = raw.filter((t): t is string => typeof t === 'string').map(parseManualCloseProblem)

  // Whole-request reasons: the request as a whole never got to sell
  // anything, so there is nothing pair-specific to list.
  if (problems.some((p) => p.kind === 'bot_halted')) {
    return { tone: 'error', text: 'The bot is halted — nothing was sold. Resolve the halt before retrying.' }
  }
  if (problems.some((p) => p.kind === 'bot_run_in_progress')) {
    return { tone: 'warning', text: 'The bot is already running for this exchange — please wait and try again.' }
  }
  if (problems.length > 0 && problems.every((p) => p.kind === 'exception')) {
    return {
      tone: 'error',
      text: 'An unexpected error stopped the sell — nothing here is confirmed sold. Check positions before retrying.',
    }
  }

  const pending = problems.filter((p) => p.kind === 'pending')
  const notSold = problems.filter((p) => p.kind !== 'pending')

  const parts: string[] = []
  if (notSold.length > 0) {
    parts.push(`not sold: ${notSold.map(pairLabel).join(', ')}`)
  }
  if (pending.length > 0) {
    parts.push(
      `outcome pending — being reconciled and may complete later, will not be re-sent: ${pending
        .map(pairLabel)
        .join(', ')}`,
    )
  }
  if (parts.length === 0) {
    // Defensive: an empty or wholly-unrecognized array must still never
    // read as success — the route only raises 409 when something is wrong.
    return { tone: 'error', text: 'Some positions were not sold. Check positions before retrying.' }
  }
  return { tone: 'error', text: `Some positions were not sold — ${parts.join('; ')}.` }
}
